import { useCallback, useMemo } from 'react'

import { MultiSelect, Select } from 'govwell-ui'
import {
  MultiSelectModeProps,
  SelectBaseProps,
  SelectOption,
  SingleSelectModeProps,
} from 'govwell-ui/components/Select/types'
import { GisSetupEntityLayerFragment } from 'types/graphql'

import { useGisSetupQuery } from 'src/fetch/organizations'

type BaseProps = Omit<SelectBaseProps<GisSetupEntityLayerFragment>, 'getOptionKey' | 'options'>

type SingleSelectProps = BaseProps & SingleSelectModeProps<GisSetupEntityLayerFragment>

type MultiSelectProps = BaseProps & MultiSelectModeProps<GisSetupEntityLayerFragment>

type Props = SingleSelectProps | MultiSelectProps

const EntityLayerSelect = (props: Props) => {
  const {
    errorMessage,
    filterOption,
    isClearable,
    isDisabled,
    isInvalid,
    isMultiSelect,
    isRequired,
    label,
    onBlur,
    onValueChange,
    placeholder,
    value,
    width,
  } = props

  const { entityLayers } = useGisSetupQuery()

  const options: SelectOption<GisSetupEntityLayerFragment>[] = useMemo(
    () =>
      (entityLayers || []).map((el) => ({
        label: el.name,
        value: el,
      })),
    [entityLayers]
  )

  const selectedIdSet = useMemo(() => {
    const selected = value == null ? [] : Array.isArray(value) ? value : [value]
    return new Set(selected.map((v) => v.id))
  }, [value])

  const selectedOptions = useMemo(() => {
    return options.filter((o) => selectedIdSet.has(o.value.id))
  }, [options, selectedIdSet])
  const selectedOption = selectedOptions[0]

  const handleSelectedOptionsChange = useCallback(
    (newSelectedOptions: SelectOption<GisSetupEntityLayerFragment>[] | undefined) => {
      if (isMultiSelect) {
        onValueChange(newSelectedOptions?.map((o) => o.value) ?? [])
      }
    },
    [isMultiSelect, onValueChange]
  )

  const handleSelectedOptionChange = useCallback(
    (newSelectedOption: SelectOption<GisSetupEntityLayerFragment> | undefined) => {
      if (!isMultiSelect) {
        onValueChange(newSelectedOption?.value)
      }
    },
    [isMultiSelect, onValueChange]
  )

  const getOptionKey = useCallback(
    (option: SelectOption<GisSetupEntityLayerFragment>) => option.value.id,
    []
  )

  const commonProps: SelectBaseProps<GisSetupEntityLayerFragment> = {
    ...(label ? { label } : { ariaLabel: 'Entity layers' }),
    errorMessage,
    filterOption,
    getOptionKey,
    isClearable,
    isDisabled,
    isInvalid,
    isRequired,
    onBlur,
    options,
    placeholder,
    width,
  }

  if (isMultiSelect) {
    return (
      <MultiSelect
        selectedOptions={selectedOptions}
        onSelectedOptionsChange={handleSelectedOptionsChange}
        {...commonProps}
      />
    )
  }

  return (
    <Select
      onSelectedOptionChange={handleSelectedOptionChange}
      selectedOption={selectedOption}
      {...commonProps}
    />
  )
}

export default React.memo(EntityLayerSelect)
